import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AnalyticsConnection,
  chooseAnalyticsProperty,
  disconnectAnalytics,
  fetchAiVisits,
  fetchAnalyticsConnection,
  fetchAnalyticsProperties,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** While the first read is landing, look again every few seconds; then stop. */
const FIRST_READ_POLL_MS = 10_000;

function stillReading(connection: AnalyticsConnection | undefined): boolean {
  return connection?.state === "connected" && connection.syncedThrough === null;
}

export function useAnalyticsConnection(workspaceId: string, brandId: string) {
  return useQuery({
    queryKey: queryKeys.analyticsConnection(workspaceId, brandId),
    queryFn: () => fetchAnalyticsConnection(workspaceId, brandId),
    refetchInterval: (query) =>
      stillReading(query.state.data) ? FIRST_READ_POLL_MS : false,
  });
}

/** Only asked for while the owner is choosing; it calls Google each time. */
export function useAnalyticsProperties(
  workspaceId: string,
  brandId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.analyticsProperties(workspaceId, brandId),
    queryFn: () => fetchAnalyticsProperties(workspaceId, brandId),
    enabled,
  });
}

function useForget(workspaceId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["analytics-connection", workspaceId] });
    await client.invalidateQueries({ queryKey: ["ai-visits", workspaceId] });
  };
}

export function useChooseAnalyticsProperty(workspaceId: string, brandId: string) {
  const client = useQueryClient();
  const forget = useForget(workspaceId);
  return useMutation({
    mutationFn: (propertyId: string) =>
      chooseAnalyticsProperty(workspaceId, brandId, propertyId),
    onSuccess: async (saved) => {
      client.setQueryData(queryKeys.analyticsConnection(workspaceId, brandId), saved);
      await forget();
    },
  });
}

export function useDisconnectAnalytics(workspaceId: string, brandId: string) {
  const forget = useForget(workspaceId);
  return useMutation({
    mutationFn: () => disconnectAnalytics(workspaceId, brandId),
    onSuccess: forget,
  });
}

export function useAiVisits(
  workspaceId: string,
  reportId: string,
  range: { readonly from: string; readonly to: string },
) {
  return useQuery({
    queryKey: queryKeys.aiVisits(workspaceId, reportId, range.from, range.to),
    queryFn: () => fetchAiVisits(workspaceId, reportId, range),
  });
}
